import { getSelectedDraftMode } from "../../BenchmarkTakeover/proseMode";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import Auth from "src/Auth";
import { buildLabelToMentionItemsMap, entityLinkSpecsFromLabelMap } from "src/components/PersistentChat/mentions/autoEntityMentions";
import TiptapListField, { getNumberedTextFromTiptapListField, getTextFromTiptapListField } from "src/components/TiptapListField";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { useAppStore } from "src/stores/appStore";
import { themeVariables } from "src/themeVariables";
import styled from "styled-components";
import { UNLIMITED_EXPERIMENT_VARIATIONS } from "../../../../../src/shared/constants.js";
import { harvestStoryBibleEntityNamesFromEntities } from "../../../../../src/shared/storyBibleEntityNames.js";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { sendInvocationEvent } from "../../../Utils";
import AutoPlaySilentVideo from "../../AutoPlaySilentVideo";
import IconButton from "../../IconButton";
import ScenesHistory from "../../ScenesHistory";
import { useDraftStateMachine, DraftStates } from "./draftStateMachine";
import ExtraInstructions from "./ExtraInstructions";
import GeneratePanel from "./GeneratePanel";
import { applyOptimisticDraftScenesEdit, createDraftScenesPersistence } from "./draftScenesPersistence";
import { useDraftSceneOverride } from "./useDraftSceneOverride";

import Header from "./Header";
import SummaryBox from "./SummaryBox";
import {
  applyGeneratePanelBottomDockedStyles,
  applyGeneratePanelFixedStyles,
  clearGeneratePanelStickyStyles,
  getDraftControlsStickyAnchorBottom,
} from "./draftGeneratePanelSticky";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { TourSignal } from "../../StickyTour/TourTypes";

// Helper function to check if scenes have content
const hasScenesContent = (documentScenes) => {
  if (!documentScenes?.value?.scenes) return false;

  const scenesText = getTextFromTiptapListField(documentScenes.value.scenes);
  return scenesText.trim().length > 0;
};

const MainContainer = styled.div`
  position: relative;
  margin: 20px;
  color: ${themeVariables.grey0};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background-color: ${themeVariables.menuColorFullscreen};
  box-shadow: ${themeVariables.dropShadow1525};
  margin-bottom: 20px;

  box-sizing: border-box;
  .divider {
    width: 100%;
    height: 1px;
    background-color: ${themeVariables.divider};
  }
`;

const ScenesHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 40px);
  padding: 4px 0px;
  .scene-label {
    font-size: 18px;
    font-weight: 500;
  }

  .scene-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }
`;

const TiptapContainer = styled.div`
  position: relative;
  z-index: 3;
  width: calc(100% - 40px);

  .recognized-entity {
    text-decoration: underline;
  }

  .recognized-entity[data-entity-id] {
    cursor: pointer;
  }
`;

function Draft({
  documentId,
  centerColumnRef,
  editorToolbarRef,
  editor,
  editorRef,
  editorEndRef,
  editableDocumentTitleRef,
  projectId,
  comparisonEditorsRef,
  comparisonInvocationIdRef,
  streamBSceneIndexRef,
  comparisonMode,
  setComparisonMode,
  setComparisonVisible,
  addDraftPlaceholderCard,
  appendDraftPlaceholderContent,
  linkDraftPlaceholderToCardId,
  finalizeDraftPlaceholder,
  removeDraftPlaceholderCard,
  onEditorLayoutChange,
}) {
  const userId = useAppStore((state) => state.user?.uid);
  const sceneToWrite = useStoryBibleStore((state) => state.sceneToWrite);
  const lastInvocation = useStoryBibleStore((state) => state.lastInvocation);
  const setLastInvocation = useStoryBibleStore((state) => state.setLastInvocation);
  const setScenesHistoryOpen = useStoryBibleStore((state) => state.setScenesHistoryOpen);
  const proseGeneratorActive = useStoryBibleStore((state) => state.proseGeneratorActive);
  const setDraftStickyBarHeight = useStoryBibleStore((state) => state.setDraftStickyBarHeight);
  const outOfQuota = useStoryBibleStore((state) => state.outOfQuota);
  const requesting = useStoryBibleStore((state) => state.requesting);
  const documentScenes = useStoryBibleStore((state) => state.documentScenes?.[documentId]);
  const draftScenesAgentRefreshNonce = useStoryBibleStore((state) => state.draftScenesAgentRefreshNonce?.[documentId] ?? 0);
  const setDocumentScenes = useStoryBibleStore((state) => state.setDocumentScenes);
  const getDocumentScenes = useStoryBibleStore((state) => state.getDocumentScenes);
  // Copy button payload: "1. …" markers so the scenes read (and re-parse) as
  // a numbered list wherever they're pasted. generateText strips numbering.
  const documentScenesText = getNumberedTextFromTiptapListField(documentScenes?.value?.scenes);
  const setPreflightResponse = useStoryBibleStore((state) => state.setPreflightResponse);
  const preflightResponse = useStoryBibleStore((state) => state.preflightResponse);
  const setRequesting = useStoryBibleStore((state) => state.setRequesting);
  const streamRequestRef = useStoryBibleStore((state) => state.streamRequestRef);
  const abortController = useStoryBibleStore((state) => state.abortController);
  const sceneId = useStoryBibleStore((state) => state.sceneId);
  const scenes = useStoryBibleStore((state) => state.scenes);
  const PROSE_MODES = useStoryBibleStore((state) => state.PROSE_MODES);
  const charactersEntities = useStoryBibleStore((state) => state.charactersEntities);
  const worldbuildingEntries = useStoryBibleStore((state) => state.worldbuildingEntries);
  const metadata = useStoryBibleStore((state) => state.metadata);
  const subscription = useSudowriteStore((state) => state.subscription);
  const globalDraftModel = useSudowriteStore((state) => state.editorOptions?.lastUsedDraftModelGlobal ?? state.draftDefaults?.lastUsedDraftModelGlobal ?? state.userData?.data?.lastUsedDraftModelGlobal);
  const streamRequest = streamRequestRef.current;
  const [documentScenesString, setDocumentScenesString] = useDraftSceneOverride(documentId, draftScenesAgentRefreshNonce);
  const [isStreamingScenes, setIsStreamingScenes] = useState(false);
  const scene = scenes?.[sceneId];
  const initialState = scene?.restartFromIndex == null ? "default" : "postGeneratedIdle";
  const draftStateMachine = useDraftStateMachine(initialState);
  const sceneSummary = sceneToWrite?.summary;
  const generatePanelRef = useRef(null);
  const spacerRef = useRef(null);
  const directControlRef = useRef(false);
  const bottomScrollingRef = useRef(false);
  const mainContainerRef = useRef(null);
  const updateDraftStickyLayoutRef = useRef(null);

  const clearLastInvocation = useStoryBibleStore((state) => state.clearLastInvocation);
  const isInUnlimitedExperiment = useSudowriteStore((state) => state.isInUnlimitedExperiment);
  const unlimitedExperimentVariation = useSudowriteStore((state) => state.unlimitedExperimentVariation);
  const legacyVoiceModels = useSudowriteStore((state) => state.customModels);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  const proseMode = useMemo(() => {
    // Determine base default prose mode (for experiment or standard users)
    const baseDefaultProseMode = isInUnlimitedExperiment && unlimitedExperimentVariation === UNLIMITED_EXPERIMENT_VARIATIONS.UNLIMITED_DEFAULT ? PROSE_MODES.BASIC : PROSE_MODES.EXCELLENT;

    const selectedMode = getSelectedDraftMode({
      globalModel: globalDraftModel,
      projectModel: metadata?.draftProseMode,
      sceneModel: scene?.proseMode,
      proseModes: PROSE_MODES,
      fallbackMode: baseDefaultProseMode,
      allowComparisonAlpha: !!subscription?.hasAccessToMuse2AlphaPhase2,
      legacyVoiceModels,
    });
    if (selectedMode && (selectedMode.benchmarkMode || !selectedMode.muse2Alpha)) return selectedMode;

    return baseDefaultProseMode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    globalDraftModel,
    metadata?.draftProseMode,
    scene?.proseMode,
    isInUnlimitedExperiment,
    unlimitedExperimentVariation,
    PROSE_MODES,
    legacyVoiceModels,
    subscription?.hasAccessToMuse2AlphaPhase2,
  ]);


  useEffect(() => {
    draftStateMachine.events.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on document switch
  }, [documentId]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        //
        !editorToolbarRef.current ||
        !generatePanelRef.current ||
        !centerColumnRef.current ||
        !mainContainerRef.current ||
        !editorEndRef.current ||
        !editorRef.current
      )
        return;

      const toolbarRect = editorToolbarRef.current.getBoundingClientRect();
      const stickyAnchorBottom = getDraftControlsStickyAnchorBottom(toolbarRect);
      const panelRect = generatePanelRef.current.getBoundingClientRect();
      const mainContainerRect = mainContainerRef.current.getBoundingClientRect();
      const editorEndRect = editorEndRef.current.getBoundingClientRect();
      const editorRect = editorRef.current.getBoundingClientRect();

      const toolbarEditorEndOffset = 100;

      // Reset when the draft panel is fully visible again
      if (mainContainerRect.bottom - panelRect.height >= stickyAnchorBottom && directControlRef.current) {
        directControlRef.current = false;
        bottomScrollingRef.current = false;
        clearGeneratePanelStickyStyles(generatePanelRef.current);
        setDraftStickyBarHeight(0);
        if (spacerRef.current) {
          spacerRef.current.style.height = "0px";
        }
        if (mainContainerRef.current) {
          mainContainerRef.current.style.boxShadow = "";
        }
        return;
      }

      // Enter or maintain sticky mode while the draft panel scrolls past the toolbar
      if (mainContainerRect.bottom - panelRect.height < stickyAnchorBottom) {
        if (!directControlRef.current) {
          if (spacerRef.current) {
            spacerRef.current.style.height = `${panelRect.height}px`;
          }
          if (mainContainerRef.current) {
            mainContainerRef.current.style.boxShadow = "none";
          }
          directControlRef.current = true;
          setDraftStickyBarHeight(panelRect.height);
        }

        const shouldBottomDock = editorEndRect.top - toolbarEditorEndOffset <= stickyAnchorBottom;
        bottomScrollingRef.current = shouldBottomDock;

        if (shouldBottomDock) {
          applyGeneratePanelBottomDockedStyles(
            generatePanelRef.current,
            mainContainerRect,
            editorRect,
            panelRect,
          );
        } else {
          applyGeneratePanelFixedStyles(generatePanelRef.current, mainContainerRect, toolbarRect);
        }
      }
    };

    updateDraftStickyLayoutRef.current = handleScroll;

    const centerColumn = centerColumnRef.current;
    if (centerColumn) {
      centerColumn.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();
      return () => {
        updateDraftStickyLayoutRef.current = null;
        centerColumn.removeEventListener("scroll", handleScroll);
        if (generatePanelRef.current) {
          clearGeneratePanelStickyStyles(generatePanelRef.current);
        }
        directControlRef.current = false;
        bottomScrollingRef.current = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs + stable panel layout
  }, [editorToolbarRef, generatePanelRef, centerColumnRef, mainContainerRef]);

  // Scroll to top when prose generator is active; reset sticky bar height when deactivated
  useEffect(() => {
    if (proseGeneratorActive) {
      centerColumnRef.current.scrollTo({ top: 0, behavior: "smooth" });
      const refreshAfterOpen = window.setTimeout(() => {
        updateDraftStickyLayoutRef.current?.();
      }, 350);
      return () => window.clearTimeout(refreshAfterOpen);
    }

    setDraftStickyBarHeight(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only when proseGeneratorActive toggles
  }, [proseGeneratorActive]);

  // Recompute sticky draft controls when SW2 chrome layout shifts (toolbar collapse, style guide).
  useEffect(() => {
    if (!proseGeneratorActive) {
      return undefined;
    }

    const refreshStickyLayout = () => {
      updateDraftStickyLayoutRef.current?.();
    };

    const projectMenu = document.querySelector(".project-menu");
    const projectRoot = document.querySelector("#project.inner, #project .inner.sw2, #project .inner");

    const mutationObserver = new MutationObserver(refreshStickyLayout);
    if (projectMenu) {
      mutationObserver.observe(projectMenu, { attributes: true, attributeFilter: ["class"] });
    }
    if (projectRoot) {
      mutationObserver.observe(projectRoot, { attributes: true, attributeFilter: ["class"] });
    }

    window.addEventListener("resize", refreshStickyLayout);
    projectRoot?.addEventListener("transitionend", refreshStickyLayout);

    return () => {
      mutationObserver.disconnect();
      window.removeEventListener("resize", refreshStickyLayout);
      projectRoot?.removeEventListener("transitionend", refreshStickyLayout);
    };
  }, [proseGeneratorActive]);

  // Handle sticky panel geometry updates when the center column resizes
  useEffect(() => {
    const handleResize = () => {
      if (!generatePanelRef.current || !mainContainerRef.current || !directControlRef.current) return;

      const mainContainerRect = mainContainerRef.current.getBoundingClientRect();
      const toolbarRect = editorToolbarRef.current?.getBoundingClientRect();

      if (bottomScrollingRef.current) {
        if (!editorRef.current) return;
        const editorRect = editorRef.current.getBoundingClientRect();
        const panelRect = generatePanelRef.current.getBoundingClientRect();
        applyGeneratePanelBottomDockedStyles(
          generatePanelRef.current,
          mainContainerRect,
          editorRect,
          panelRect,
        );
        return;
      }

      if (!toolbarRect) return;
      applyGeneratePanelFixedStyles(generatePanelRef.current, mainContainerRect, toolbarRect);
    };

    const centerColumn = centerColumnRef.current;
    if (centerColumn) {
      // Use ResizeObserver to detect width changes
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(centerColumn);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [centerColumnRef, generatePanelRef, mainContainerRef, directControlRef]);

  useEffect(() => {
    const load = async () => {
      if (!documentScenes && documentId) {
        const scenes = await Auth.fetchWithIdToken(`/api/scenes/`, {
          documentId: documentId,
        });
        if (!getDocumentScenes(documentId)) setDocumentScenes(documentId, scenes);
      }
    };
    load();
  }, [documentId, documentScenes, getDocumentScenes, setDocumentScenes]);

  const captureSceneSaveError = useCallback((error) => {
    Sentry.captureException(error, {
      tags: { feature: "draft_scenes_persistence" },
      extra: { documentId },
    });
  }, [documentId]);

  const scenePersistence = useMemo(() => createDraftScenesPersistence({
    documentId,
    getDocumentScenes,
    setDocumentScenes,
    onError: captureSceneSaveError,
  }), [documentId, getDocumentScenes, setDocumentScenes, captureSceneSaveError]);

  const flushPendingSceneSave = useCallback(() => {
    scenePersistence.flush().catch(captureSceneSaveError);
  }, [scenePersistence, captureSceneSaveError]);

  useEffect(() => {
    if (!proseGeneratorActive) {
      flushPendingSceneSave();
    }
  }, [proseGeneratorActive, flushPendingSceneSave]);

  useEffect(() => {
    return () => {
      flushPendingSceneSave();
    };
  }, [flushPendingSceneSave]);

  useEffect(() => {
    if (!draftScenesAgentRefreshNonce) return;
    scenePersistence.cancel();
  }, [draftScenesAgentRefreshNonce, scenePersistence]);

  const entityNames = useMemo(
    () => {
      const labelMap = buildLabelToMentionItemsMap({
        characters: charactersEntities,
        worldbuilding: worldbuildingEntries,
        documents: [],
        hasOutline: false,
      });
      const recognizedNames = harvestStoryBibleEntityNamesFromEntities({
        charactersEntities,
        worldbuildingEntries,
      });
      return entityLinkSpecsFromLabelMap(labelMap, recognizedNames);
    },
    [charactersEntities, worldbuildingEntries],
  );

  const generateScenes = () => {
    setIsStreamingScenes(true);
    setRequesting(true);

    const url = "/api/ai/storyEngine/generate-scenes";
    streamRequest(
      url,
      // additionalData
      {
        sceneSummary, // Pass the sceneSummary to the streamRequest
        sceneId,
        scenesEngine: proseMode.scenesEngine,
      },
      // update
      (text) => {
        if (text) {
          setDocumentScenesString(text);
        }
      },
      // abortSignal
      abortController.current.signal,
      // requestType
      `commands-${sceneId}`,
      // onChunk
      null,
      // onEnd
      () => {
        sendFlowSignal(TourSignal.DRAFT_SCENES_GENERATE_COMPLETED);
        setRequesting(false);
        setIsStreamingScenes(false);
        flushPendingSceneSave();
        draftStateMachine.events.scenesEdited();
      },
      // onTimeout
      null,
      // onStart
      null,
      // onError: a request rejected before any text (for example an access
      // check) otherwise left the scenes loader cycling forever. Keep the
      // wrapper's toast.
      () => {
        setRequesting(false);
        setIsStreamingScenes(false);
        return false;
      }
    );
    return true;
  };
  const handleScenesRestore = (jsonContent) => {
    setDocumentScenesString(jsonContent);
  };

  const openScenesHistory = async () => {
    setScenesHistoryOpen(true);
    await Auth.fetchWithIdToken(`/api/scenes/snapshot`, {
      documentId,
      projectId,
    });
  };

  if (!proseGeneratorActive) return null;

  return (
    <MainContainer
      ref={mainContainerRef}
      key={`draft-container-${documentId}`}
    >
      <ScenesHistory
        documentId={documentId}
        onRestore={handleScenesRestore}
      />
      <Header />
      {sceneSummary ? (
        <SummaryBox
          sceneSummary={sceneSummary}
          sceneToWrite={sceneToWrite}
        />
      ) : (
        <div className="divider" />
      )}
      <ScenesHeader>
        <div className="scene-label">Scenes</div>
        <div className="scene-actions">
          <CopyToClipboard
            text={documentScenesText}
            onCopy={() => {
              if (lastInvocation) {
                sendInvocationEvent(lastInvocation, "copy");
              }
            }}
            options={{ format: "text/plain" }}
          >
            <IconButton
              src="/assets/duplicate.svg"
              className="copy"
              disabled={!documentScenesText}
              ariaLabel="Copy to Clipboard"
            ></IconButton>
          </CopyToClipboard>
          <IconButton
            src="/assets/clock.svg"
            disabled={requesting}
            onClick={openScenesHistory}
            size={30}
            ariaLabel="Show History"
          />
        </div>
      </ScenesHeader>
      {documentScenes && (
        <TiptapContainer>
          <TiptapListField
            key={`draft-scenes-editor-${documentId}-${draftScenesAgentRefreshNonce}`}
            setLastInvocation={setLastInvocation}
            requesting={requesting}
            outOfQuota={outOfQuota}
            onChange={(json, shouldClearLastInvocation = true) => {
              // Only save for actual content changes, not mark-only changes
              // (entity underlines). Mark-only changes are UI decorations and
              // persisting them causes a save → sync → entity effect loop.
              if (shouldClearLastInvocation) {
                applyOptimisticDraftScenesEdit({
                  documentId,
                  scenesJson: json,
                  getDocumentScenes,
                  setDocumentScenes,
                  saveDocumentScenes: scenePersistence.schedule,
                });
              }
              if (preflightResponse && shouldClearLastInvocation) {
                setPreflightResponse(null);
              }

              // this guards against cases where changes to the editor are either
              // from streaming or Known Entities mark creation so we don't want
              // to clear the last invocation (used for the prose-generated-from-scenes metric)
              if (!isStreamingScenes && shouldClearLastInvocation) {
                clearLastInvocation("commands");
              }

              // Only reset preflight review for actual user edits, not mark-only
              // changes (e.g. entity visualization updates)
              if (draftStateMachine.value === "preflightReview" && shouldClearLastInvocation) {
                draftStateMachine.events.scenesEdited();
                clearLastInvocation("commands");
              }
            }}
            value={documentScenes?.value?.scenes || {}}
            updateValueString={documentScenesString}
            generate={generateScenes}
            generateLabel="Generate Scenes"
            generateDisabled={isStreamingScenes && !!sceneSummary}
            generateDisabledMessage={"Generating Scenes..."}
            entityNames={entityNames}
            tooltipContent={
              !sceneSummary ? (
                <div className="w-full flex flex-col gap-4 items-center justify-center rounded-xl">
                  <AutoPlaySilentVideo
                    mp4URL="https://sudoart.s3.us-east-2.amazonaws.com/sudowrite-images/link-to-outline.mp4"
                    webmURL="https://sudoart.s3.us-east-2.amazonaws.com/sudowrite-images/link-to-outline.webm"
                    className="w-full h-auto rounded-md"
                    immediate={true}
                  />
                  <p className="text-center">
                    To Generate Scenes, <strong>link this document to your Outline.</strong> That way Sudowrite knows what kind of Scenes to Generate.
                  </p>
                </div>
              ) : (
                ""
              )
            }
            disabled={!sceneSummary}
            userId={userId}
            projectId={projectId}
          />
        </TiptapContainer>
      )}
      <ExtraInstructions
        documentId={documentId}
        projectId={projectId}
        saveDocumentScenes={scenePersistence.schedule}
        disabled={draftStateMachine.value === DraftStates.PREFLIGHT_RUNNING}
        draftStateMachine={draftStateMachine}
      />
      <GeneratePanel
        ref={generatePanelRef}
        editor={editor}
        editorRef={editorRef}
        editorEndRef={editorEndRef}
        documentId={documentId}
        projectId={projectId}
        setDocumentScenesString={setDocumentScenesString}
        draftStateMachine={draftStateMachine}
        setIsStreamingScenes={setIsStreamingScenes}
        proseMode={proseMode}
        missingScenesAndNotConnected={!sceneSummary && !hasScenesContent(documentScenes)}
        scenePersistence={scenePersistence}
        comparisonEditorsRef={comparisonEditorsRef}
        comparisonInvocationIdRef={comparisonInvocationIdRef}
        streamBSceneIndexRef={streamBSceneIndexRef}
        setComparisonMode={setComparisonMode}
        setComparisonVisible={setComparisonVisible}
        addDraftPlaceholderCard={addDraftPlaceholderCard}
        appendDraftPlaceholderContent={appendDraftPlaceholderContent}
        linkDraftPlaceholderToCardId={linkDraftPlaceholderToCardId}
        finalizeDraftPlaceholder={finalizeDraftPlaceholder}
        removeDraftPlaceholderCard={removeDraftPlaceholderCard}
        onEditorLayoutChange={onEditorLayoutChange}
      />
      <div
        ref={spacerRef}
        style={{ height: 0 }}
      />
    </MainContainer>
  );
}

export default Draft;
